<!--
  @name: EchartsBoxBar
  @description: echarts线下图基础样式组件
-->
<template>
  <custom-echarts-box
    ref="echartsRef"
    v-bind="$attrs"
    :option="defaultBasicOption"
    :loading="loading"
  />
</template>
<script setup lang="ts">
import * as echarts from 'echarts/core';
import { PropType, reactive, ref, watch } from 'vue';
import CustomEchartsBox from './index.vue';
import { type ECOption, type SeriesOption } from './setupEcharts';
import { calMax, calMin, isNull } from '@core/tools';

defineOptions({ name: 'CustomEchartsBar' });

const props = defineProps({
  dataSet: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  // 系列柱状配置
  seriesOption: {
    type: Object as PropType<SeriesOption>,
    default: () => ({} as SeriesOption),
  },
  // 系列折线配置
  lineSeriesOption: {
    type: Object as PropType<SeriesOption>,
    default: () => ({} as SeriesOption),
  },
  // 图表配置
  option: {
    type: Object as PropType<ECOption>,
    default: () => ({} as ECOption),
  },
});

const echartsRef = ref();

// 每个系列默认配置 柱状图
// const defaultSeriesOption = reactive<SeriesOption>({
//   name: '代理数量',
//   type: 'bar',
//   barGap: 0,
//   barMaxWidth: 30,
//   barMinWidth: 10,
//   itemStyle: { color: '#2D76FF' },
// });
const defaultSeriesOption = reactive<SeriesOption>({
  name: '代理数量',
  type: 'line',
  smooth: true, // 是否平滑
  itemStyle: { color: '#2D76FF' },
  areaStyle: {
    opacity: 0.8,
    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
      {
        offset: 0,
        color: '#2D76FF60',
      },
      {
        offset: 0,
        color: '#2D76FF30',
      },
      {
        offset: 1,
        color: '#2D76FF00',
      },
    ]),
  },
});

// 每个系列默认配置
const defaultLineSeriesOption = reactive<SeriesOption>({
  name: '企业数量',
  type: 'line',
  yAxisIndex: 1,
  smooth: true, // 是否平滑
  itemStyle: { color: '#78DFB2' },
  areaStyle: {
    opacity: 0.8,
    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
      {
        offset: 0,
        color: '#78DFB2',
      },
      {
        offset: 0,
        color: '#91FFAE30',
      },
      {
        offset: 1,
        color: '#91FFAE00',
      },
    ]),
  },
});

// echarts默认基础图表配置
const defaultBasicOption = ref<ECOption>({
  // 标题组件
  title: {},
  // 网格组件
  grid: {
    left: 0,
    right: 0,
    top: 40,
    bottom: 0,
    containLabel: true,
  },
  // 控制图标组件
  legend: {
    // type: 'plain',
    // icon: 'rect',
    // padding: [20, 0, 0, 0],
    // align: 'right',
    // top: 'top',
    right: '0',
    top: '-0',
    // itemWidth: 16,
    // itemHeight: 6,
    textStyle: {
      color: '#00000099',
      fontSize: 12,
    },
  },
  tooltip: {
    trigger: 'axis',
    padding: 16,
    textStyle: {
      fontSize: 12,
      color: '#000',
    },
    axisPointer: { type: 'none' },
  },
  xAxis: {
    type: 'category',
    // boundaryGap: false, //是否设置边界
    nameGap: 35,
    axisTick: { show: false },
    axisLine: {
      // 轴线配置
      show: false,
      lineStyle: { color: '#e7e7e7' },
    },
    axisLabel: { color: '#00000066' },
  },
  yAxis: [
    {
      type: 'value',
      name: '',
      nameGap: 20,
      nameTextStyle: {
        color: '#000',
        fontSize: 14,
        lineHeight: 22,
        align: 'center',
      },
      axisLabel: {
        formatter: function(v) {
          return `￥${v.toFixed(0)}`; // 0表示小数为0位，1表示1位小数，2表示2位小数
        }, 
      },
    },
    {
      type: 'value',
      name: '',
      nameGap: 20,
      nameTextStyle: {
        color: '#000',
        fontSize: 14,
        lineHeight: 22,
        align: 'center',
      },
      axisLabel: {
        formatter: function(v) {
          return `${v.toFixed(0)}笔`; // 0表示小数为0位，1表示1位小数，2表示2位小数
        }, 
      },
    },
  ],
  dataZoom: {
    type: 'inside',
    start: 0,
    // endValue: 6, // 设置超过传入数据的索引值的数据进行滑动
    zoomLock: true,
  },
  series: [],
});

watch(
  () => props.option,
  (newV) => {
    echartsRef.value?.getEcInstance().setOption(newV);
  },
  { immediate: true },
);

watch(
  () => props.seriesOption,
  (newV) => {
    Object.assign(defaultSeriesOption, newV);
  },
  { immediate: true },
);
watch(
  () => props.lineSeriesOption,
  (newV) => {
    Object.assign(defaultLineSeriesOption, newV);
  },
  { immediate: true },
);

watch(
  () => props.dataSet,
  (newV) => {
    if (!isNull(newV) && newV.length > 0) {
      // 调用方法，获取数据的最大值&最小值
      var minData1 = calMin(newV[1]) < 0 ? 0 : calMin(newV[1]);
      var minData2 = calMin(newV[2]) < 0 ? 0 : calMin(newV[2]);
      var maxData1 = calMin(newV[1]) < 0 ? calMax(newV[1]) + Math.abs(calMin(newV[1])) : calMax(newV[1]);
      var maxData2 = calMin(newV[2]) < 0 ? calMax(newV[2]) + Math.abs(calMin(newV[2])) : calMax(newV[2]);

      echartsRef.value?.getEcInstance().setOption(
        {
          xAxis: { data: newV[0] },
          yAxis: [
            {
              min: minData1,
              max: maxData1,
              splitNumber: 6, // 设置坐标轴的分割段数
              interval: (maxData1 - minData1) / 6, // 标轴分割间隔
            },
            {
              min: minData2,
              max: maxData2,
              splitNumber: 6,
              interval: (maxData2 - minData2) / 6,
            },
          ],
          series: [Object.assign({ data: newV[1] }, defaultSeriesOption), Object.assign({ data: newV[2] }, defaultLineSeriesOption)], 
        },
        { replaceMerge: 'series' },
      );
    }
  },
  { immediate: true },
);
</script>
